import L from "leaflet";
import type { PlanWeekDatapoint } from "../state/api.types";
import { toTitleCase } from "./stringHelpers";

/**
 * Analytics constants
 */
export const ENROLLMENTS_DEFAULT_DAYS = 42; // 6 weeks for enrollments
export const STATS_DEFAULT_DAYS = 30; // 30 days for stats (upcoming/recently activated)

/** Chart colors as hex values for Recharts */
export const CHART_COLORS = [
  { dark: "#0090A8", light: "#4DE7FF" }, // Cyan
  { dark: "#CA8504", light: "#FFF04D" }, // Yellow
  { dark: "#C11574", light: "#FF33FF" }, // Magenta
  { dark: "#2E1E93", light: "#4D33F5" }, // Blue
  { dark: "#D4122C", light: "#F5334D" }, // Red/Orange
];

/** Y-axis padding (5% above max value) */
export const CHART_Y_AXIS_PADDING = 1.05;

/** Chart tooltip cursor color */
export const CHART_CURSOR_COLOR = "#A4A7AE";

const CHART_DOT_COLORS = CHART_COLORS.map((color) => color.dark);
export const CHART_AREA_COLORS = CHART_COLORS.map((color) => ({
  fill: color.light,
  stroke: color.dark,
}));

export function getPlanDotColor(
  planName: string,
  planNameColorOrder: string[],
): string {
  const originalIndex = planNameColorOrder.indexOf(planName);
  const colorIndex = planNameColorOrder.length - 1 - originalIndex;
  return CHART_DOT_COLORS[colorIndex % CHART_DOT_COLORS.length];
}

/**
 * Format positive number to display whole numbers or round to 1 decimal place.
 * @param value - A non-negative number (e.g., energy readings)
 */
export function formatNumber(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1);
}

export const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  compactDisplay: "short",
});

/** Check if chart has no data (empty or all zeros). */
export function hasNoChartData(
  data: PlanWeekDatapoint[],
  planNames: string[],
): boolean {
  if (data.length === 0) return true;

  return data.every((dataPoint) =>
    planNames.every((planName) => (dataPoint.plan_counts[planName] ?? 0) === 0),
  );
}

/** Location data types */
export type TransformedLocation = {
  id: string;
  lat: number;
  lng: number;
  city: string;
  metroArea: string | null;
  count: number;
};

export type ClusteredLocation = TransformedLocation & {
  isCluster: boolean;
  clusterSize: number;
  locations: TransformedLocation[];
  name: string;
};

/** Transform location data from API format to component format */
export function transformLocationData(
  data: {
    city: string;
    state: string;
    latitude: number;
    longitude: number;
    metro_area?: string | null;
    active_locations: number;
  }[],
): TransformedLocation[] {
  return data.map((location, index) => ({
    id: `${location.city}-${location.state}-${String(index)}`,
    lat: location.latitude,
    lng: location.longitude,
    city: toTitleCase(location.city),
    metroArea: location.metro_area ? toTitleCase(location.metro_area) : null,
    count: location.active_locations,
  }));
}

/** Cluster nearby locations based on zoom level */
export function clusterLocations(
  locations: TransformedLocation[],
  zoom: number,
): ClusteredLocation[] {
  const distanceThreshold =
    zoom <= 5 ? 100 : zoom === 6 ? 50 : zoom === 7 ? 25 : 0;

  if (distanceThreshold === 0) {
    return locations.map((loc) => ({
      ...loc,
      name: loc.city,
      isCluster: false,
      clusterSize: 1,
      locations: [loc],
    }));
  }

  const clusters: ClusteredLocation[] = [];
  const used = new Set<string>();

  locations.forEach((location) => {
    if (used.has(location.id)) return;

    const cluster: TransformedLocation[] = [location];
    used.add(location.id);

    locations.forEach((other) => {
      if (used.has(other.id)) return;

      if (zoom <= 5) {
        if (
          location.metroArea &&
          other.metroArea &&
          location.metroArea !== other.metroArea
        ) {
          return;
        }
      }

      // Use Leaflet's built-in distance calculation (haversine formula)
      const distanceInKm =
        L.latLng(location.lat, location.lng).distanceTo(
          L.latLng(other.lat, other.lng),
        ) / 1000;

      if (distanceInKm <= distanceThreshold) {
        cluster.push(other);
        used.add(other.id);
      }
    });

    const centerLat =
      cluster.reduce((sum, loc) => sum + loc.lat, 0) / cluster.length;
    const centerLng =
      cluster.reduce((sum, loc) => sum + loc.lng, 0) / cluster.length;
    const totalCount = cluster.reduce((sum, loc) => sum + loc.count, 0);

    const metroAccounts = new Map<string, number>();
    const cityAccounts = new Map<string, number>();

    cluster.forEach((loc) => {
      if (loc.metroArea) {
        metroAccounts.set(
          loc.metroArea,
          (metroAccounts.get(loc.metroArea) ?? 0) + loc.count,
        );
      }
      cityAccounts.set(loc.city, (cityAccounts.get(loc.city) ?? 0) + loc.count);
    });

    let displayName: string;

    if (cluster.length === 1) {
      if (zoom <= 5 && cluster[0].metroArea) {
        displayName = cluster[0].metroArea;
      } else {
        displayName = cluster[0].city;
      }
    } else if (zoom <= 5 && metroAccounts.size > 0) {
      const mostAccountsMetro = Array.from(metroAccounts.entries()).reduce(
        (a, b) => (a[1] > b[1] ? a : b),
      )[0];
      displayName = mostAccountsMetro;
    } else {
      const sortedCities = Array.from(cityAccounts.entries()).sort(
        (a, b) => b[1] - a[1],
      );
      const uniqueCityNames = sortedCities.map((entry) => entry[0]);

      if (uniqueCityNames.length === 1) {
        displayName = uniqueCityNames[0];
      } else if (uniqueCityNames.length === 2) {
        displayName = `${uniqueCityNames[0]}, ${uniqueCityNames[1]}`;
      } else {
        displayName = `${uniqueCityNames[0]}, ${uniqueCityNames[1]}, ...`;
      }
    }

    clusters.push({
      id: cluster.map((l) => l.id).join("-"),
      lat: centerLat,
      lng: centerLng,
      city: cluster[0].city,
      name: displayName,
      metroArea: cluster[0].metroArea,
      count: totalCount,
      isCluster: cluster.length > 1,
      clusterSize: cluster.length,
      locations: cluster,
    });
  });

  return clusters;
}

export const CHART_RIGHT_MARGIN_PX = 64;
export const CHART_TOP_MARGIN_PX = 76;
